import { useQuery } from "@tanstack/react-query";
import { StickyNote } from "lucide-react";
import { useState } from "react";
import type { CustomerSummary, FieldDefinition } from "@estelle/domain";

import { useDataSource } from "@/adapters/data-source";
import { PhoneNumber } from "@/components/phone/PhoneNumber";
import { LoadFailed, NoMatches } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { RecordDrawer } from "@/components/shell/RecordDrawer";
import { DataTable } from "@/components/table/DataTable";
import { Pagination } from "@/components/table/Pagination";
import { useCursorHistory } from "@/components/table/useCursorHistory";
import { Badge } from "@/components/ui/badge";
import {
  dateOnly,
  money,
  ordersLabel,
} from "@/modules/contacts/customers/format";
import { projectCustomer } from "@/modules/contacts/customers/projections";
import { CustomerDetail } from "@/modules/contacts/customers/screens/CustomerDetail";
import { CUSTOMER_KEYS } from "@/modules/contacts/customers/useCustomerAnnotations";
import type { SectionScreenProps } from "@/modules/types";
import { useOpenRecord } from "@/routes/useOpenRecord";

/** The name, with a note icon when the team has written one on this customer. */
function NameCell({ customer }: { customer: CustomerSummary }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span>{customer.displayName || "—"}</span>
      {customer.note ? (
        <StickyNote
          aria-label="Has a note"
          className="text-muted-foreground size-3.5 shrink-0"
        />
      ) : null}
    </span>
  );
}

/**
 * One customer as a card on a phone: the name and what they have spent on the
 * first line, where they are and how many orders on the second. The generic
 * label/value stack would put ten rows under every name.
 */
function CustomerMobileCard({ customer }: { customer: CustomerSummary }) {
  const place = [customer.city, customer.state].filter(Boolean).join(", ");
  return (
    <div className="min-w-0">
      <div className="flex items-baseline justify-between gap-3">
        <span className="truncate text-sm font-medium">
          <NameCell customer={customer} />
        </span>
        <span className="shrink-0 text-sm font-medium tabular-nums">
          {money(customer.totalSpent)}
        </span>
      </div>
      <div className="text-muted-foreground mt-0.5 truncate text-sm">
        {[place || null, ordersLabel(customer.ordersCount)]
          .filter(Boolean)
          .join(" • ")}
      </div>
      {customer.lastOrderAt ? (
        <div className="text-muted-foreground mt-0.5 text-xs">
          Last order {dateOnly(customer.lastOrderAt)}
        </div>
      ) : null}
      {customer.tags.length > 0 ? (
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {customer.tags.slice(0, 4).map((tag) => (
            <Badge key={tag} variant="outline" className="font-normal">
              {tag}
            </Badge>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The Customers list (#447): Shopify's customers with the orders-derived
 * figures and the team's own tags and notes.
 *
 * Filter, sort, search, columns and paging are the shared ones — this screen
 * only says what a row is and what opens it. Which customer is open lives in
 * the URL (`?record=`), so an order can link straight to one.
 */
export function CustomerList({
  fields,
  filter,
  search,
  sort,
  onToggleSort,
  onClearFilter,
}: SectionScreenProps) {
  const data = useDataSource();

  const [limit, setLimit] = useState(25);
  /** Paging stays local, as in every list — see `OrdersList`. */
  const [history, setHistory] = useCursorHistory();
  const cursor = history[history.length - 1];
  const { recordId, open } = useOpenRecord();

  const query = useQuery({
    queryKey: [...CUSTOMER_KEYS.list, filter, search, sort, limit, cursor],
    queryFn: () => data.customers.list({ filter, search, sort, limit, cursor }),
  });

  /*
    The open customer, for the drawer's title when this page of the list does
    not hold the row — a link opened cold, from an order or a shared URL. The
    same query, under the same key, as `CustomerDetail` makes, so it is one
    request, not two; it only lets the title say the name instead of "Customer"
    once that has arrived.
  */
  const openCustomer = useQuery({
    queryKey: CUSTOMER_KEYS.detail(recordId ?? ""),
    queryFn: () => data.customers.get(recordId ?? ""),
    enabled: recordId !== null,
  });

  function renderOverride(field: FieldDefinition, customer: CustomerSummary) {
    if (field.name === "displayName") return <NameCell customer={customer} />;
    // One format and a Call button, as everywhere (#635).
    if (field.name === "phone" && customer.phone) {
      return (
        <PhoneNumber
          number={customer.phone}
          name={customer.displayName}
          className="flex-nowrap whitespace-nowrap"
        />
      );
    }
    return undefined;
  }

  if (query.isPending) return <TableSkeleton />;

  if (query.isError) {
    return (
      <LoadFailed
        message={query.error.message}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <DataTable
        fields={fields}
        rows={query.data.items}
        getRowId={(customer) => customer.id}
        project={projectCustomer}
        sort={sort}
        onToggleSort={onToggleSort}
        onRowClick={(customer) => {
          open(customer.id);
        }}
        renderOverride={renderOverride}
        mobileRow={(customer) => <CustomerMobileCard customer={customer} />}
        emptyState={<NoMatches onClear={onClearFilter} />}
        footer={
          <Pagination
            page={query.data}
            limit={limit}
            onLimitChange={(next) => {
              setLimit(next);
              setHistory([undefined]);
            }}
            history={history}
            onNavigate={setHistory}
          />
        }
      />

      {/*
        Beside the table rather than instead of it, so closing the drawer
        costs no refetch. The title comes from the row where the list holds
        it; a link opened cold falls back to the generic name until
        `CustomerDetail` has fetched.
      */}
      {recordId !== null ? (
        <RecordDrawer
          title={
            [
              query.data.items.find((customer) => customer.id === recordId)
                ?.displayName,
              openCustomer.data?.displayName,
            ].find((name) => name !== undefined && name.trim() !== "") ??
            "Customer"
          }
        >
          <CustomerDetail customerId={recordId} />
        </RecordDrawer>
      ) : null}
    </div>
  );
}
